import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react';
import * as turf from '@turf/turf';
import { useInView } from 'react-intersection-observer';
import { toast } from 'sonner';
import { DateTime } from 'luxon';
import MoreOptionsKebab from '@/components/ui/scout/MoreOptionsKebab';
import { sortSequencesByTimestamp } from '@/utils/sort';
import { prettyDate } from '@/utils/dates';
import {
  BurstStatus,
  Frame,
  ScoutErrorType,
  ScoutLocation,
} from '@/types/location';
import { getImagesForPolygon } from '@/api/developer';
import { stitch } from '@/utils/imagery';
import RemoveLocation from '@/components/ui/scout/RemoveLocation';
import Thumbnail from '@/components/ui/scout/imagery/Thumbnail.tsx';
import * as Toast from '@/components/ui/toast/Toast';
import { useMapCredits } from '@/hooks/useMapCredits';
import { useScout } from '@/hooks/useScout';
import { Views } from '@/types/view';
import { Button } from '@/components/shadcn/button';
import ImagerySkeleton from '@/components/ui/scout/skeleton/ImagerySkeleton';
import { validateOrganizationFunded } from '@/api/organization';
import { getLatestBurst } from '@/utils/scout';
import { BURST_PRICE } from '@/components/ui/scout/Scout';
import { useUser } from '@/hooks/useUser';

export interface SummaryProps {
  location: ScoutLocation;
  selectionCallback?: (
    id: string | number,
    lastSelectedView: Views,
    index?: number,
  ) => void;
  setBurstModalOptions: Dispatch<
    SetStateAction<{
      isOpen: boolean;
      locationsIds: string[];
      successCallback: () => void;
    }>
  >;
}

const Summary: React.FC<SummaryProps> = ({
  location,
  selectionCallback,
  setBurstModalOptions,
}) => {
  const [sortedSequences, setSortedSequences] = useState<Frame[][]>([]);
  const [apiCallsComplete, setApiCallsComplete] = useState(false);
  const [framesLength, setFramesLength] = useState(0);
  const [requestCost, setRequestCost] = useState<number | null>(null);

  const { setMapCredits, setCreditsModalOptions } = useMapCredits();
  const { setLocations } = useScout();
  const { user } = useUser();

  const latestSequence = sortedSequences[0] || [];
  const lastFrame: Frame | undefined =
    latestSequence[latestSequence.length - 1];
  const lastMapped = (lastFrame && lastFrame.timestamp) || null;
  const [errorReason, setErrorReason] = useState<ScoutErrorType>();

  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.01,
  });

  const centroid = turf.centroid(location.geojson as turf.AllGeoJSON);
  const latest = getLatestBurst(location.bursts);
  const hasActiveBurst = latest?.status === BurstStatus.ACTIVE;

  const imageryCallback = useCallback(
    (id: string) => {
      setLocations(prevState => {
        return prevState.map(location => {
          if (location._id === id) {
            return {
              ...location,
              lastViewed: new Date().toISOString(),
            };
          }

          return location;
        });
      });
    },
    [setLocations],
  );

  const handleAddBurst = async (id: string) => {
    const response = await validateOrganizationFunded(
      user?.currentOrganization,
    );

    if ('error' in response) {
      console.error(response.error);
      toast.custom(t => (
        <Toast.Default
          title="Try Again"
          description="Something went wrong, please try again."
          buttons={[
            {
              label: 'Dismiss',
              onClick: () => toast.dismiss(t),
              variant: 'secondary',
              size: 'fw',
            },
          ]}
        />
      ));
      return;
    }

    if (response.credits >= BURST_PRICE) {
      setBurstModalOptions({
        isOpen: true,
        locationsIds: [id],
        successCallback: () => {},
      });
    } else {
      toast.custom(t => (
        <Toast.Default
          title="Map Credits Needed"
          description="Purchase map credits to continue using Scout."
          buttons={[
            {
              label: 'Dismiss',
              onClick: () => toast.dismiss(t),
              variant: 'secondary',
              size: 'fw',
            },
            {
              label: 'Purchase Credits',
              onClick: () => {
                setCreditsModalOptions({ isVisible: true });
                toast.dismiss(t);
              },
              variant: 'default',
              size: 'fw',
            },
          ]}
        />
      ));
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    if (!user) {
      return;
    }

    if (inView) {
      const fetchImagery = async () => {
        setErrorReason(undefined);
        setRequestCost(null);

        const data = await getImagesForPolygon(
          location._id,
          location.geojson,
          user.currentOrganization,
          signal,
          imageryCallback,
        );

        if ('error' in data) {
          if (!signal.aborted) {
            toast.custom(t => (
              <Toast.Default
                title="Request Failed"
                description={data.error.message || data.error}
                buttons={[
                  {
                    label: 'Dismiss',
                    onClick: () => toast.dismiss(t),
                    variant: 'secondary',
                    size: 'fw',
                  },
                ]}
              />
            ));
          }

          setErrorReason('unknown');
          setApiCallsComplete(true);
          setSortedSequences([]);
          return;
        }

        const { frames, cost, creditsRemaining, notEnoughCredits } = data;
        setMapCredits(creditsRemaining ?? 0);

        if (frames.length > 0) {
          const stitched = stitch(frames);
          setSortedSequences(sortSequencesByTimestamp(stitched));
          setFramesLength(frames.length);
        } else {
          setErrorReason('imagery');
        }

        if (notEnoughCredits) {
          setErrorReason('mapCredits');
          toast.custom(t => (
            <Toast.Default
              title="Map Credits Needed"
              description="Purchase map credits to continue using Scout."
              buttons={[
                {
                  label: 'Dismiss',
                  onClick: () => toast.dismiss(t),
                  variant: 'secondary',
                  size: 'fw',
                },
                {
                  label: 'Purchase Credits',
                  onClick: () => {
                    setCreditsModalOptions({ isVisible: true });
                    toast.dismiss(t);
                  },
                  variant: 'default',
                  size: 'fw',
                },
              ]}
            />
          ));
        }

        setRequestCost(cost);
        setApiCallsComplete(true);
      };

      fetchImagery();
    }

    return () => {
      controller.abort();
    };
  }, [
    inView,
    location._id,
    location.geojson,
    user,
    setSortedSequences,
    setMapCredits,
    imageryCallback,
    setCreditsModalOptions,
  ]);

  return (
    <div
      ref={ref}
      className="flex w-full border-b bg-background pb-4 text-left">
      <div className="w-1/3">
        <div
          className="cursor-pointer font-bold"
          onClick={() => {
            selectionCallback &&
              selectionCallback(location._id, Views.Location);
          }}>
          {location.name}
        </div>
        <div className="flex flex-col gap-2 md:gap-4">
          {location.description && (
            <div className="text-xs">{location.description}</div>
          )}
          {centroid && (
            <div className="text-xs">
              {`${centroid.geometry.coordinates[1].toFixed(
                5,
              )}, ${centroid.geometry.coordinates[0].toFixed(5)}`}
            </div>
          )}
          {sortedSequences.length > 0 && (
            <div className="text-xs">
              {sortedSequences.length} collections, {framesLength} images
            </div>
          )}
          {lastMapped && (
            <div className="text-xs">Mapped {prettyDate(lastMapped, true)}</div>
          )}
        </div>
      </div>
      <div className="relative flex w-2/3 flex-row pt-10">
        <div className="absolute right-0 top-0 flex items-center gap-4">
          {requestCost !== null && requestCost > 0 && (
            <div className="inline-flex h-[30px] w-[62px] items-center justify-center gap-2 rounded-full border border-azure-400 px-4 py-2">
              <div className="text-xs font-bold">Fresh</div>
            </div>
          )}
          {latest && hasActiveBurst ? (
            <div className="inline-flex h-[30px] items-center justify-center gap-2 rounded-full border border-harvestGoldWarning-600 px-4 py-2">
              <div className="text-xs font-bold">
                Burst expires{' '}
                {DateTime.fromISO(latest.validUntil, {
                  zone: 'utc',
                }).toFormat('LLL dd, yyyy')}
              </div>
            </div>
          ) : (
            <Button
              variant="burst"
              onClick={() => handleAddBurst(location._id)}>
              Burst
            </Button>
          )}
          <MoreOptionsKebab
            elements={[
              <RemoveLocation
                key={location._id}
                id={location._id}
                setLocations={setLocations}
              />,
            ]}
          />
        </div>
        {sortedSequences.length > 0 ? (
          <div
            className="no-scrollbar mt-3 flex w-full flex-row overflow-x-scroll focus:outline-none"
            tabIndex={0}>
            {sortedSequences.map((sequence: Frame[], index) => {
              const { url, timestamp } = sequence[0];
              const isLast = index === sortedSequences.length - 1;

              return (
                <div
                  data-last={isLast}
                  key={url}
                  className="mr-2 min-w-[60%] data-[last=true]:mr-0 md:min-w-[30%]">
                  <Thumbnail
                    url={url}
                    timestamp={timestamp}
                    onClick={() => {
                      selectionCallback &&
                        selectionCallback(location._id, Views.Location, index);
                    }}
                  />
                </div>
              );
            })}
          </div>
        ) : (
          <ImagerySkeleton
            apiCallsComplete={apiCallsComplete}
            errorReason={errorReason}
            id={location._id}
            setBurstModalOptions={setBurstModalOptions}
            hasActiveBurst={hasActiveBurst}
          />
        )}
      </div>
    </div>
  );
};

export default Summary;
